/**
 * Reusable panel adapter for demos that embed a ManuscriptViewer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  buildManuscriptFromFiles,
  composeChapterLoaders,
  type BuiltManuscript,
  type ManuscriptBuilderOptions,
} from "../manuscript-builder.js";
import { ManuscriptViewer } from "./ManuscriptViewer.js";
import type { LoadChapterFn, Manuscript, ManuscriptViewerProps } from "./types.js";

type ForwardedViewerProps = Omit<
  ManuscriptViewerProps,
  "manuscripts" | "loadChapter" | "unavailableMessage"
>;

export interface ManuscriptViewerPanelOptions extends ForwardedViewerProps {
  readonly manuscripts: readonly Manuscript[];
  readonly loadChapter: LoadChapterFn;
  readonly unavailableMessage?: string;
  readonly unavailablePath?: string;
  readonly displayName?: string;
}

export interface BuiltManuscriptViewerPanelOptions extends ForwardedViewerProps {
  readonly unavailableMessage?: string;
  readonly unavailablePath?: string;
  readonly displayName?: string;
}

export interface FileBackedManuscriptSource {
  readonly manuscriptOptions: ManuscriptBuilderOptions;
  readonly files: Record<string, string>;
}

function defaultUnavailableMessage(path: string | undefined): string {
  if (path === undefined) {
    return "Chapter file not bundled. Ensure the manuscript file exists in the configured source path.";
  }
  return `Chapter file not bundled. Ensure the manuscript file exists under ${path}.`;
}

export function createManuscriptViewerPanel({
  manuscripts,
  loadChapter,
  unavailableMessage,
  unavailablePath,
  displayName = "ManuscriptPanel",
  ...viewerProps
}: ManuscriptViewerPanelOptions): React.ComponentType {
  function GeneratedManuscriptPanel(): React.JSX.Element {
    return (
      <ManuscriptViewer
        {...viewerProps}
        manuscripts={manuscripts}
        loadChapter={loadChapter}
        unavailableMessage={unavailableMessage ?? defaultUnavailableMessage(unavailablePath)}
      />
    );
  }
  GeneratedManuscriptPanel.displayName = displayName;
  return GeneratedManuscriptPanel;
}

export function createBuiltManuscriptViewerPanel(
  built: BuiltManuscript,
  options: BuiltManuscriptViewerPanelOptions = {},
): React.ComponentType {
  return createManuscriptViewerPanel({
    manuscripts: [built.manuscript],
    loadChapter: built.loadChapter,
    ...options,
  });
}

export function createBuiltManuscriptViewerPanelFromFiles(
  manuscriptOptions: ManuscriptBuilderOptions,
  files: Record<string, string>,
  panelOptions: BuiltManuscriptViewerPanelOptions = {},
): React.ComponentType {
  return createBuiltManuscriptViewerPanel(
    buildManuscriptFromFiles(manuscriptOptions, files),
    panelOptions,
  );
}

export function createFileBackedManuscriptCollectionPanel(
  sources: readonly FileBackedManuscriptSource[],
  panelOptions: BuiltManuscriptViewerPanelOptions = {},
): React.ComponentType {
  const built = sources.map((source) =>
    buildManuscriptFromFiles(source.manuscriptOptions, source.files),
  );

  return createManuscriptViewerPanel({
    manuscripts: built.map((source) => source.manuscript),
    loadChapter: composeChapterLoaders(built.map((source) => source.loadChapter)),
    ...panelOptions,
  });
}
